import { createFileRoute, Link } from '@tanstack/react-router';

export const Route = createFileRoute('/services/forest-transition')({
  component: ForestTransitionPage,
  head: () => ({
    meta: [
      { title: 'Native forest transition — Warren-Blackwood Electorate Guide' },
      {
        name: 'description',
        content:
          'Plain-English guide to WA\'s Native Forestry Transition Plan, retraining and redeployment support, and the local services for forestry workers, contractors and timber-town communities affected by the 2024 closure.',
      },
      { property: 'og:title', content: 'Native forest transition — Warren-Blackwood' },
      {
        property: 'og:description',
        content:
          'Native Forestry Transition Plan, Jobs and Skills Centres, retraining and redeployment for forestry workers, contractors and small businesses.',
      },
    ],
  }),
});

function ForestTransitionPage() {
  const h2: React.CSSProperties = {
    fontFamily: 'var(--font-heading)',
    fontSize: '1.5rem',
    color: 'var(--rivergum)',
    marginTop: 'var(--sp-xl)',
    marginBottom: 'var(--sp-sm)',
    borderBottom: '1px solid var(--light-border)',
    paddingBottom: '0.3rem',
    letterSpacing: '0.02em',
  };
  const h3: React.CSSProperties = {
    fontFamily: 'var(--font-heading)',
    fontSize: '1.1rem',
    color: 'var(--ink)',
    marginTop: 'var(--sp-md)',
    marginBottom: '0.4rem',
    letterSpacing: '0.02em',
  };
  const p: React.CSSProperties = {
    color: 'var(--ink)',
    lineHeight: 1.65,
    marginBottom: 'var(--sp-sm)',
  };
  const link: React.CSSProperties = { color: 'var(--green-mid)', fontWeight: 600 };
  const callout = (bg: string, border: string): React.CSSProperties => ({
    background: bg,
    border: `1px solid ${border}`,
    borderRadius: 8,
    padding: 'var(--sp-md)',
    marginBottom: 'var(--sp-md)',
  });

  return (
    <main
      id="main-content"
      style={{
        maxWidth: 760,
        margin: '0 auto',
        padding: 'var(--sp-lg) var(--sp-md) var(--sp-xl)',
      }}
    >
      <p
        style={{
          textTransform: 'uppercase',
          letterSpacing: '0.12em',
          fontSize: '0.75rem',
          fontWeight: 700,
          color: 'var(--green-mid)',
          margin: 0,
          marginBottom: '0.35rem',
        }}
      >
        Services · Native forest transition
      </p>
      <h1
        style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'clamp(2rem, 5vw, 2.75rem)',
          color: 'var(--green)',
          margin: 0,
          marginBottom: 'var(--sp-md)',
          lineHeight: 1.1,
        }}
      >
        Native forest transition
      </h1>
      <p
        style={{
          color: 'var(--ink-mid)',
          fontStyle: 'italic',
          fontSize: '1.05rem',
          marginBottom: 'var(--sp-md)',
          lineHeight: 1.5,
        }}
      >
        On 1 January 2024 the WA Government ended large-scale native forest
        logging in our region's karri, jarrah and wandoo forests. That
        decision reshaped local economies in Manjimup, Pemberton, Northcliffe,
        Bridgetown and surrounding timber towns. This page is a plain-English
        guide to the transition support available and where to go for help.
      </p>

      <div style={callout('#eef5fb', '#b9d4e8')}>
        <strong style={{ color: '#1b4f72' }}>If you need help today</strong>
        <p style={{ ...p, margin: '0.4rem 0 0', fontSize: '0.95rem' }}>
          <a href="https://www.southregionaltafe.wa.edu.au/jsc" style={link}>Jobs and Skills Centres</a>{' '}
          are in Bunbury, Collie and Albany. Regional residents can book on{' '}
          <a href="tel:136464" style={link}>13 64 64</a>.
          <br />
          Rural Financial Counselling Service WA —{' '}
          <a href="tel:1800612004" style={link}>1800 612 004</a> (free, confidential — contractors and small businesses welcome)
          <br />
          Rurallink (after-hours mental health, rural WA) —{' '}
          <a href="tel:1800552002" style={link}>1800 552 002</a>
          <br />
          Beyond Blue — NewAccess for Small Business Owners —{' '}
          <a href="tel:1300945301" style={link}>1300 945 301</a>
        </p>
      </div>

      <h2 style={h2}>What changed in 2024</h2>
      <p style={p}>
        The WA Government announced in September 2021 that large-scale
        commercial native forest logging would end across WA from
        <strong> 1 January 2024</strong>, replaced by softwood plantation
        expansion and a smaller-scale ecological thinning program. The
        decision affects karri, jarrah, wandoo and other native species
        across our region — historically the heart of WA's native timber
        industry.
      </p>
      <p style={p}>
        Limited harvesting continues for specific purposes — ecological
        thinning, forest health, fire-mitigation works, mine site clearance,
        and small-scale specialty timber for craft and furniture — under the
        new <a href="https://www.wa.gov.au/government/publications/forest-management-plan-2024-2033" style={link}>Forest Management Plan 2024–2033</a>.
      </p>

      <h2 style={h2}>The Native Forestry Transition Plan</h2>
      <p style={p}>
        The WA Government's <strong>Native Forestry Transition Plan</strong>{' '}
        is the package of support for workers, contractors, small businesses,
        Aboriginal businesses and timber-town communities affected by the
        change. It's been expanded several times since 2021 and now includes:
      </p>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          <strong>Worker Programs</strong> — case-managed support,
          redundancy assistance, retraining funding and redeployment for
          directly-employed forestry workers.
        </li>
        <li>
          <strong>Business Programs</strong> — funding for affected
          haulage, harvesting and small timber-processing contractors to
          help them wind down or pivot.
        </li>
        <li>
          <strong>Industry and Community Development Programs</strong> —
          investment in softwood plantation expansion, value-add wood
          processing, new industries for timber towns, and projects led by
          affected local governments to support economic diversification.
        </li>
      </ul>
      <p style={p}>
        <a
          href="https://www.wa.gov.au/organisation/department-of-energy-and-economic-diversification/native-forest-transition"
          style={link}
        >
          Native Forest Transition — wa.gov.au →
        </a>
      </p>
      <div style={callout('#fff7e6', '#f0c674')}>
        <p style={{ ...p, margin: 0, fontSize: '0.92rem', color: '#7a5300' }}>
          All Native Forest Transition Plan programs are now closed — the
          last, Closure of Impacted Businesses, closed on 31 December 2025.
          For questions, contact the Department of Energy and Economic
          Diversification (DEED) on{' '}
          <a href="tel:0861866976" style={link}>(08) 6186 6976</a> or{' '}
          <a href="mailto:nft@deed.wa.gov.au" style={link}>nft@deed.wa.gov.au</a>.
        </p>
      </div>

      <h2 style={h2}>Jobs and Skills Centres</h2>
      <p style={p}>
        <strong>Jobs and Skills Centres are in Bunbury, Collie and
        Albany. Regional residents can book on{' '}
        <a href="tel:136464" style={link}>13 64 64</a>.</strong>{' '}
        They are the one-stop front door for retraining, course
        information, fee help, resume support and connection with
        employers. The service is <strong>free</strong> and open to
        workers, contractors and family members.
      </p>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          Free careers and training advice — including credit for prior
          experience (RPL) and pathways out of forestry into adjacent
          industries.
        </li>
        <li>
          Information on <strong>Lower Fees, Local Skills</strong>{' '}
          (subsidised TAFE courses) and Jobs and Skills WA programs.
        </li>
        <li>
          Connection to local employers — including DFES, Parks &amp;
          Wildlife Service, agriculture, road maintenance, mining services
          and trades.
        </li>
        <li>
          Help with workforce-mobility supports if relocation for work is
          on the table.
        </li>
      </ul>
      <p style={p}>
        Phone <a href="tel:136464" style={link}>13 64 64</a> ·{' '}
        <a href="https://www.southregionaltafe.wa.edu.au/jsc" style={link}>
          Find your nearest Jobs and Skills Centre →
        </a>
      </p>

      <h2 style={h2}>Retraining &amp; redeployment pathways</h2>
      <p style={p}>
        Many former forestry workers in Warren-Blackwood have already moved
        into adjacent skilled work. The most common pathways:
      </p>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          <strong>DFES &amp; Parks and Wildlife Service</strong> — fire
          mitigation, prescribed burning and bushfire response. The skills,
          equipment use and bush knowledge transfer well. DFES recruits
          career firefighters, volunteer brigade officers and bushfire
          mitigation crews.{' '}
          <a href="https://www.dfes.wa.gov.au/careers" style={link}>dfes.wa.gov.au/careers →</a>
        </li>
        <li>
          <strong>Plantation forestry</strong> — softwood plantation
          establishment, harvesting and processing is expanding under the
          transition plan.
        </li>
        <li>
          <strong>Civil construction &amp; road works</strong> — Main Roads
          and contractor crews need machinery operators, traffic
          management and supervisors.
        </li>
        <li>
          <strong>Mining services</strong> — Boddington, Greenbushes and
          Collie are within drive-in/drive-out range; truck driving,
          plant operation and trades are in demand.
        </li>
        <li>
          <strong>Agriculture &amp; horticulture</strong> — Manjimup and
          surrounding farms need farm hands, machinery operators and
          irrigation specialists.
        </li>
        <li>
          <strong>Trades</strong> — adult-apprenticeship pathways into
          carpentry, plumbing, electrical and diesel mechanics, with fee
          subsidies for eligible mature-age workers.
        </li>
      </ul>
      <p style={p}>
        A small clarification: <strong>DFES</strong> stands for the
        Department of Fire and Emergency Services — they handle hiring
        directly. The state government's retraining and skills funding
        sits with the <strong>Department of Training and Workforce
        Development</strong>, delivered through Jobs and Skills Centres
        and TAFE.
      </p>

      <h2 style={h2}>Support for contractors &amp; small businesses</h2>
      <p style={p}>
        Many directly-affected operators were owner-drivers, family
        contracting outfits or small sawmills — not employees of the big
        FPC operations. Specific supports:
      </p>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          <strong>Rural Financial Counselling Service WA</strong> —{' '}
          <a href="tel:1800612004" style={link}>1800 612 004</a> — free
          confidential financial counselling, including for forestry
          contractors and small primary-sector businesses.
        </li>
        <li>
          <strong>Small Business Development Corporation (SBDC)</strong> —{' '}
          <a href="tel:133140" style={link}>13 31 40</a> — free advisory,
          dispute resolution and business-pivot planning. See the{' '}
          <Link to="/services/small-business" style={link}>small business guide</Link>{' '}
          for the full picture.
        </li>
        <li>
          <strong>Grants finder</strong> — the{' '}
          <Link to="/services/grants" style={link}>grants page</Link> lists
          current federal and state funding for small business, community
          infrastructure and economic diversification projects.
        </li>
      </ul>

      <h2 style={h2}>Mental health &amp; family support</h2>
      <p style={p}>
        The loss of a multigenerational industry is felt well beyond
        payroll. These services are warm, free, and built for rural
        Australians:
      </p>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          <strong>Rurallink</strong> —{' '}
          <a href="tel:1800552002" style={link}>1800 552 002</a> — after-hours
          mental health triage specifically for rural WA.
        </li>
        <li>
          <strong>Beyond Blue NewAccess for Small Business Owners</strong> —{' '}
          <a href="tel:1300945301" style={link}>1300 945 301</a> — six free
          coaching sessions, no GP referral.
        </li>
        <li>
          <strong>Lifeline</strong> —{' '}
          <a href="tel:131114" style={link}>13 11 14</a> — 24/7 crisis support.
        </li>
        <li>
          <strong>13YARN</strong> —{' '}
          <a href="tel:139276" style={link}>13 92 76</a> — 24/7 crisis line
          for First Nations Australians.
        </li>
      </ul>

      <h2 style={h2}>Bevan's position &amp; advocacy</h2>
      <p style={p}>
        Bevan represents communities where forestry was a multigenerational
        livelihood — directly in Manjimup, Pemberton, Northcliffe,
        Bridgetown and Greenbushes, and indirectly across haulage,
        machinery and processing supply chains throughout the electorate.
      </p>
      <p style={p}>
        Bevan's consistent position in Parliament and locally has been that
        the transition was announced with insufficient consultation and
        notice for affected workers, contractors and small towns, and that
        ongoing delivery of the Transition Plan must be measured not by
        dollars committed but by real outcomes for affected families. His
        advocacy focuses on:
      </p>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          Holding the government to account on case-by-case follow-through
          for workers and contractors who are still navigating the change.
        </li>
        <li>
          Pressing for genuine local economic diversification — not just
          short-term project funding — in the most-affected shires.
        </li>
        <li>
          Ensuring the smaller specialty timber, craft and furniture
          industries that survived the change have the regulatory clarity
          they need to operate.
        </li>
        <li>
          Advocating for ongoing skilled fire-mitigation crews on
          public land — both as a workforce opportunity and a bushfire-risk
          imperative for the region.
        </li>
        <li>
          Raising community concerns about access to public land, including
          for recreation, prospecting and traditional small-scale firewood
          collection, where the new arrangements have created confusion.
        </li>
      </ul>
      <p style={{ ...p, fontSize: '0.88rem', color: 'var(--ink-mid)' }}>
        For specific statements on current questions in Parliament or
        media, see Bevan's <Link to="/contact" style={link}>contact page</Link>{' '}
        and recent media releases. If a quote or position on this page is
        out of date, the office will update it on request.
      </p>

      <h2 style={h2}>If you need help from the electorate office</h2>
      <p style={p}>
        The electorate office can assist with:
      </p>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          Stalled or unclear interactions with the Department of Training
          and Workforce Development, Forest Products Commission or DBCA.
        </li>
        <li>
          Cases where the published transition support hasn't translated
          into actual assistance for an individual or business.
        </li>
        <li>
          Escalating systemic issues — gaps in eligibility, town-level
          impacts, supply-chain fall-out — to the relevant Minister.
        </li>
        <li>
          Connecting people with the right local service if you're not sure
          where to start.
        </li>
      </ul>
      <p style={p}>
        See the <Link to="/contact" style={link}>contact page</Link> for the
        electorate office hours and the best way to get in touch.
      </p>

      <h2 style={h2}>Trusted sources</h2>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          <a
            href="https://www.wa.gov.au/organisation/department-of-energy-and-economic-diversification/native-forest-transition"
            style={link}
          >
            wa.gov.au — Native Forest Transition
          </a>
        </li>
        <li>
          <a href="https://www.wa.gov.au/government/publications/forest-management-plan-2024-2033" style={link}>
            Forest Management Plan 2024–2033
          </a>
        </li>
        <li>
          <a href="https://www.jobsandskills.wa.gov.au/" style={link}>jobsandskills.wa.gov.au</a>{' '}
          — Jobs and Skills WA, including Lower Fees, Local Skills.
        </li>
        <li>
          <a href="https://www.dbca.wa.gov.au/" style={link}>dbca.wa.gov.au</a> —
          Department of Biodiversity, Conservation and Attractions (manages
          our forests).
        </li>
        <li>
          <a href="https://www.fpc.wa.gov.au/" style={link}>fpc.wa.gov.au</a> —
          Forest Products Commission.
        </li>
      </ul>

      <p
        style={{
          marginTop: 'var(--sp-lg)',
          fontSize: '0.78rem',
          color: 'var(--ink-light)',
          borderTop: '1px solid var(--rule)',
          paddingTop: 'var(--sp-sm)',
          lineHeight: 1.5,
        }}
      >
        Community summary of publicly-available information from
        wa.gov.au, the Department of Training and Workforce Development,
        DBCA and the Forest Products Commission. Programs and contacts
        change — confirm details on the official sites above before
        acting. Bevan's position is summarised in good faith from his
        public statements; for the current parliamentary record,{' '}
        <Link to="/contact" style={{ color: 'var(--green-mid)', fontWeight: 500 }}>contact the office</Link>
        . Spot something out of date?{' '}
        <a
          href="mailto:bevan.eatts@mp.wa.gov.au?subject=Warren-Blackwood%20guide%20%E2%80%94%20forest%20transition%20update"
          style={{ color: 'var(--green-mid)', fontWeight: 500 }}
        >
          Email the office
        </a>
        . Last reviewed: June 2026.
      </p>
    </main>
  );
}
